/* eslint-disable logical-assignment-operators */
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Reorder } from './types.js';
import { broker } from '#~/agent/index.js';
import { StudioError } from '#~/error/index.js';

/**
 * Reorder the devices on the specified agent.
 *
 * @param deviceIds - New device order.
 * @param devices - Devices to reorder.
 * @param token - The token.
 */
export const reorder: Reorder = async (deviceIds, devices, token) => {
  try {
    const rankedIds = Object.fromEntries(
      deviceIds.map((deviceId, index) => [
        deviceId,
        { agentId: devices[deviceId]?.agentId, rank: index }
      ])
    );

    const mutations: Record<Agent['id'], Record<string, DeepPartial<Device>>> = {};

    for (const [deviceId, { agentId, rank }] of Object.entries(rankedIds)) {
      if (!agentId) {
        continue;
      }

      const mutation = mutations[agentId] ?? (mutations[agentId] = {});

      mutation[deviceId] = { rank };
    }

    const promises = Object.entries(mutations).map(async ([agentId, mutation]) => {
      const proxy = broker.acquire(<Agent['id']>agentId);

      if (!proxy) {
        throw new Error('RD-843');
      }

      await proxy.mutate(
        {
          control: {
            studio: {
              device: mutation
            }
          }
        },
        {}
      );

      await proxy.save(token);
    });

    await Promise.all(promises);
  } catch (cause) {
    throw new StudioError('Unable to reorder devices.', { cause });
  }
};
